import { COOKIE_KEYS, getCookie } from "@portal/app/_common/lib/headers";
import md5 from "crypto-js/md5";

export const EXPERIMENTS = {
  EMAIL_NOTIFICATIONS: {
    0: "Disabled",
    1: "Enabled",
  },
  COMPLETED_LEAD_EVENT: {
    0: "Purchase",
    1: "Generate lead",
  },
  /**
   * Kill switch for deferring the homepage DreamSearch fetch until the cards
   * are on screen or the visitor scrolls. Ships Enabled. Flipping the weight
   * map below only changes the default for visitors without an existing
   * `dream_experiments` cookie — getExperimentsFromId spreads that cookie's
   * value after the weight-map default, so anyone already bucketed into
   * Enabled keeps it indefinitely. To force Disabled for everyone
   * immediately, set `resolvedExperiments.HOMEPAGE_DEFERRED_DREAM_SEARCH = 0`
   * below instead.
   */
  HOMEPAGE_DEFERRED_DREAM_SEARCH: {
    0: "Disabled",
    1: "Enabled",
  },
} as const;

const EXPERIMENT_WEIGHT_MAP: Partial<ExperimentWeightMap> = {
  EMAIL_NOTIFICATIONS: {
    0: 100,
    1: 0,
  },
  COMPLETED_LEAD_EVENT: {
    0: 0,
    1: 100,
  },
  HOMEPAGE_DEFERRED_DREAM_SEARCH: {
    0: 0,
    1: 100,
  },
};

/**
 * Forces an experiment to a fixed value for every visitor, overriding both
 * the weight map and any per-visitor cookie override. Use this as the
 * instant kill switch when a weight-map flip alone would not affect
 * visitors already bucketed via a stale `dream_experiments` cookie.
 */
export const resolvedExperiments: Partial<ResolvedExperiments> = {
  // Enable completed-lead routing for every visitor, including stale purchase
  // assignments. Set this to 0 and restore purchase weights to roll back.
  COMPLETED_LEAD_EVENT: 1,
};

/**
 * Overrides applied when the user has an admin role (i.e. Dreambound staff).
 * Use this to dogfood features internally before rolling them out.
 */
export const ADMIN_OVERRIDES: Partial<ResolvedExperiments> = {};

/**
 *
 * END CONFIGURING EXPERIMENTS
 *
 */

export type Experiments<
  AE extends Record<string, Record<number, string>> = typeof EXPERIMENTS,
  E extends keyof AE = keyof AE,
> = Record<E, ExperimentValue<AE, E>>;

/** Applies only to senders migrating from purchase; hosted leads already use generate_lead. */
export const shouldUseGenerateLead = (experiments?: Partial<Experiments>): boolean =>
  (resolvedExperiments.COMPLETED_LEAD_EVENT ?? experiments?.COMPLETED_LEAD_EVENT ?? 0) === 1;

export type ExperimentValue<
  AE extends Record<string, Record<number, string>> = typeof EXPERIMENTS,
  E extends keyof AE = keyof AE,
> = keyof AE[E];

type ExperimentWeightMap<AE extends typeof EXPERIMENTS = typeof EXPERIMENTS> = {
  [E in keyof AE]?: {
    [K in keyof AE[E]]: number;
  };
};

type ResolvedExperiments<AE extends typeof EXPERIMENTS = typeof EXPERIMENTS> = {
  [E in keyof AE]?: ExperimentValue<AE, E>;
};

export type ExperimentValueDescription<
  AE extends typeof EXPERIMENTS,
  E extends keyof AE = keyof AE,
> = AE[E][ExperimentValue<AE, E>];

export function getExperimentValueFromDescription<
  AE extends typeof EXPERIMENTS,
  E extends keyof AE = keyof AE,
>(
  experiment: E,
  valueDescription: ExperimentValueDescription<AE, E>,
  /**
   * Used exclusively for testing.
   */
  _allExperiments = EXPERIMENTS as AE
): ExperimentValue<AE, E> {
  return Number(
    Object.entries(((_allExperiments as AE)[experiment] ?? {}) as object).find(
      ([, name]) => name === valueDescription
    )?.[0]
  ) as ExperimentValue<AE, E>;
}

export const toFriendlyExperiments = <AE extends typeof EXPERIMENTS>(
  experiments: Partial<Experiments<AE>>,
  /**
   * Used exclusively for testing.
   */
  _allExperiments = EXPERIMENTS as AE
) =>
  Object.fromEntries(
    Object.entries(experiments)
      .map(([key, value]) => {
        const experimentOptions = _allExperiments[
          key as keyof typeof _allExperiments
        ] as unknown as AE | undefined;
        return [key, experimentOptions?.[value as keyof AE]];
      })
      .filter(([, value]) => value !== undefined)
  );

export const getExperimentsFromId = <AE extends typeof EXPERIMENTS>(
  id: string,
  overrides: Partial<Experiments<AE>>,
  {
    isAdmin,
    _allExperiments = EXPERIMENTS as AE,
    _weightMap = EXPERIMENT_WEIGHT_MAP as ExperimentWeightMap<AE>,
  }: {
    isAdmin?: boolean;
    /** Used exclusively for testing. */
    _allExperiments?: AE;
    /** Used exclusively for testing. */
    _weightMap?: ExperimentWeightMap<AE>;
  } = {}
): Experiments<AE> => {
  const defaultExperiments: Experiments<AE> = (
    Object.keys(_allExperiments) as Array<keyof AE>
  ).reduce(
    (acc, key) => ({
      ...acc,
      [key]: getExperimentGroupFromId<AE>(id, key, _allExperiments, _weightMap[key]),
    }),
    {}
  ) as Experiments<AE>;

  return {
    ...defaultExperiments,
    /**
     * Overrides could be outdated, so we need to filter out experiment values
     * that are not defined.
     */
    ...Object.fromEntries(
      Object.entries(overrides).filter(([key, value]) => {
        const experiment = (_allExperiments as AE)[key as keyof AE] as AE[keyof AE] | undefined;
        return experiment?.[value as keyof typeof experiment] !== undefined;
      })
    ),
    ...resolvedExperiments,
    ...(isAdmin ? ADMIN_OVERRIDES : {}),
  };
};

/**
 * Returns the group number from an ID (customer, user...). The group number is
 * a 0-based index of the total number of groups.
 * If weightMap is provided, then each group is weighted accordingly.
 *
 * Exported only for testing
 * @param id
 * @param experiment
 * @param _experiments
 * @param _weightMap
 */
export const getExperimentGroupFromId = <
  AE extends typeof EXPERIMENTS,
  E extends keyof AE = keyof AE,
>(
  id: string,
  experiment: E,
  /**
   * Used exclusively for testing.
   */
  _experiments = EXPERIMENTS as AE,
  _weightMap?: Record<ExperimentValue<AE>, number>
): ExperimentValue<AE> => {
  const totalGroups = Object.keys(
    (_experiments[experiment] ?? {}) as Record<string, number>
  )?.length;
  const hexMD5hash = md5(`${(experiment as string) + id}`)
    .toString()
    .substr(0, 8);
  const hashAsInt = parseInt("0x" + hexMD5hash, 16);
  const maxInt = parseInt("0xffffffff", 16);
  const hash = Math.floor((hashAsInt / maxInt) * 100);
  if (!_weightMap) {
    return (hash % totalGroups) as ExperimentValue<AE>;
  }

  if (Object.values<number>(_weightMap).reduce((acc, weight) => acc + weight, 0) !== 100) {
    throw new Error("Weights must add up to 100");
  }
  /**
   * Creates slots for each group.
   * For example, if the weight map is { 0: 10, 1: 30, 2: 60 }, `weightedSlots`
   * would be [[0, 10], [10, 40], [40, 100]].
   * If the `hashGroup` is 7, then it would return the first slot, group 0.
   * If the `hashGroup` is 83, then it would return the third slot, group 2...
   */
  const weightedSlots = Object.values<number>(_weightMap).reduce(
    (acc: [number, number][], weight: number, i) => {
      const lowerBound = acc[i - 1]?.[1] ?? 0;
      acc.push([lowerBound, lowerBound + weight]);
      return acc;
    },
    [] as [number, number][]
  );

  /**
   * Matches the hashGroup to the slots, and returns the group number.
   */
  const hashGroup = hash % 100; // hashGroup is an integer between 0 and 99
  return weightedSlots.reduce(
    (acc, [lowerBound, upperBound], i) =>
      hashGroup >= lowerBound && hashGroup < upperBound ? (i as ExperimentValue<AE>) : acc,
    0 as ExperimentValue<AE>
  );
};

export const getClientExperiments = <
  AE extends Record<string, Record<string, string>> = typeof EXPERIMENTS,
  E extends keyof AE = keyof AE,
>(): Experiments<AE, E> => {
  const rawExperiments = getCookie(COOKIE_KEYS.EXPERIMENTS);
  return parseExperiments<AE, E>(rawExperiments);
};

export const parseExperiments = <
  AE extends Record<string, Record<string, string>> = typeof EXPERIMENTS,
  E extends keyof AE = keyof AE,
>(
  rawExperiments: string | undefined
): Experiments<AE, E> => {
  return JSON.parse(rawExperiments ? decodeURIComponent(rawExperiments) : "{}");
};
